import { Add } from 'iconsax-react';
import { filter, isNil, sortBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flyout from '@/components/common/Flyout';
import { FilePreview } from '@/components/common/file-preview';
import ProgressButton from '@/components/common/progress-button';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useSystemSetting from '@/hooks/files/use-system-setting';
import { addToast, addTranslateToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType, getFileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import type { ConversationFormDataType } from '../conversation-form';

type ItemType<T> = T extends (infer U)[] ? U : never;
type AttachmentItemType = ItemType<ConversationFormDataType['Attachment']>;

type Props = {
  files: AttachmentItemType[];
  fileLimit: number;
  onCancel: () => void;
  onPick: (type: FileType, capture?: 'camera' | 'album') => void;
};

interface ExtendsFileType extends AttachmentItemType {
  uuid: string;
  order?: number;
}

export default function FileSelectorFlyout({ files, fileLimit, onCancel, onPick }: Props) {
  const [show, setShow] = useState(false);
  const [innerFiles, setInnerFiles] = useState<ExtendsFileType[]>([]);
  const { t } = useTranslation();
  const { isAdminSession } = useConversationStore();
  const {
    form: { setValue, reset, formState },
    submit,
  } = useConversationForm();
  const { getFileSizeLimit } = useSystemSetting();

  useEffect(() => {
    if (files.length === 0) {
      setShow(false);
    } else {
      let unpassedFilesCount = 0;
      setInnerFiles(
        files.map((file, idx) => {
          let order: number | undefined;
          if (idx < fileLimit && file.size < getFileSizeLimit(file.type.split('/')[0], isAdminSession)) {
            order = idx - unpassedFilesCount;
          } else {
            unpassedFilesCount++;
          }
          return {
            ...file,
            order,
            uuid: `${Date.now()}_${idx}`,
          };
        }),
      );
      setShow(true);
    }
  }, [fileLimit, files, isAdminSession]);

  const selectedFiles = useMemo(() => innerFiles.filter((file) => file.order !== undefined), [innerFiles]);

  const handleClick = (file: ExtendsFileType) => () => {
    const isSelect = file.order !== undefined;

    const maxOrder = selectedFiles.length - 1;

    if (!isSelect && selectedFiles.length >= fileLimit) return;

    const fileSizeLimit = getFileSizeLimit(file.type.split('/')[0], isAdminSession);
    if (file.size > fileSizeLimit) {
      addToast({
        type: 'error',
        message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
      });
      return;
    }
    if (!isSelect) {
      setInnerFiles((prev) => prev.map((f) => (f.uuid === file.uuid ? { ...f, order: maxOrder + 1 } : f)));
      return;
    }

    const selectedOrder = file.order ?? 0;
    setInnerFiles((prev) =>
      prev.map((f) =>
        f.uuid === file.uuid
          ? { ...f, order: undefined }
          : f.order && f.order > selectedOrder
            ? { ...f, order: f.order - 1 }
            : f,
      ),
    );
  };

  const handleSubmit = () => {
    const selectedFiles = sortBy(
      filter(innerFiles, (file) => !isNil(file.order)),
      'order',
    );

    if (selectedFiles.find((s) => s.size === 0)) {
      return addTranslateToast({
        type: 'error',
        message: 'errors.invalid-file',
      });
    }

    setValue('Attachment', selectedFiles);
    submit();
    reset();
    setShow(false);
  };

  return (
    <Flyout
      containerClassName='desktop:justify-center'
      className='m-0 !pb-0 desktop:max-w-screen-sm'
      shouldLockScroll={false}
      show={show}
      onClose={() => {}}>
      <div className='flex max-h-70dvh w-full flex-col gap-2 desktop:gap-4 rounded-t-xl desktop:rounded-b-xl bg-neutral-0 px-1.5 pt-3 pb-safe-offset-8 desktop:p-6'>
        <div className='flex w-full items-center justify-between px-3.5'>
          <span className='font-bold'>
            {t('upload.selected')}
            {` ${selectedFiles.length} `}
          </span>

          <button
            type='button'
            className='btn btn-ghost text-primary btn-sm'
            onClick={() => {
              setInnerFiles([]);
              setShow(false);
              onCancel();
            }}>
            {t('common.cancel')}
          </button>
        </div>

        <div className='grid flex-1 desktop:gap-2 grid-cols-3 gap-1 overflow-auto'>
          {innerFiles.map((file) => {
            const fileSizeLimit = getFileSizeLimit(file.type.split('/')[0], isAdminSession);

            return (
              <div
                key={file.uuid}
                className='relative size-full h-fit'
                onClick={handleClick(file)}>
                <FilePreview
                  file={file}
                  fileType={getFileType(file)}
                  className='aspect-square object-cover object-center pointer-events-none'
                />

                <div
                  className={cn(
                    'absolute right-2 top-2 flex size-5 items-center justify-center rounded-full border border-neutral-50',
                    file.order === undefined ? 'bg-secondary/10' : 'bg-primary text-sm text-inverse',
                    file.size > fileSizeLimit ? 'border-error' : '',
                  )}>
                  {file.order !== undefined ? file.order + 1 : ''}
                </div>
              </div>
            );
          })}

          <button
            type='button'
            className={cn(
              'aspect-square cursor-pointer m-1 rounded-lg border-2 border-dashed bg-transparent border-metal-500 flex items-center justify-center hover:bg-metal-50 transition-colors',
            )}
            onClick={() => onPick(innerFiles[0] ? getFileType(innerFiles[0]) : FileType.File)}>
            <Add className='size-8 text-metal-500' />
          </button>
        </div>

        <div className={cn(selectedFiles.length ? '' : 'hidden')}>
          <ProgressButton
            as='button'
            className='btn btn-primary w-full'
            onClick={handleSubmit}
            label='upload.send'
            isLoading={formState.isSubmitting}
          />
        </div>
      </div>
    </Flyout>
  );
}
